import { scrubPostHogErrorEvent } from '@shared/ui/observability/posthog-error-scrub';
import { resetStorageOnVersionChange } from '@shared/ui/storage/version-reset';
import posthog from 'posthog-js';
import { startTransition, StrictMode } from 'react';
import { hydrateRoot } from 'react-dom/client';
import { HydratedRouter } from 'react-router/dom';
import { registerSW } from 'virtual:pwa-register';

import { registerStaleChunkRecovery } from './modules/core/stale-chunk';

// First thing on boot, before hydration restores the persisted react-query cache:
// if this is a new build, drop all local + session storage so stale-shaped cached
// data can't crash the app (TAS-90; TAS-89 was the crash). Keeps the auth cookie and
// the theme mirror. Must run before <QueryProvider> mounts. See version-reset.ts.
resetStorageOnVersionChange(__BUILD_ID__);

// Register the PWA service worker — client only (this file never runs during SSR).
// autoUpdate: a new deploy's SW takes over and the tab reloads once for the fresh
// precache. No-op in dev (devOptions.enabled is false). See vite.config.ts.
if (import.meta.env.PROD) {
	registerSW({ immediate: true });

	// Safety net for the classic post-deploy failure: an in-app navigation imports
	// a lazy chunk whose hashed asset a newer deploy has replaced (404). One
	// guarded reload re-fetches the current HTML + chunks. Prod only — a full
	// reload would fight dev HMR. See stale-chunk.ts for the loop guard.
	registerStaleChunkRecovery();
}

// Dev-only accessibility check: axe-core scans the rendered page and
// logs any WCAG violations to the console. Runs on axe-core directly (works on
// any React version); dynamically imported so it never ships to prod.
if (import.meta.env.DEV) {
	void import('axe-core').then(({ default: axe }) => {
		setTimeout(() => {
			void axe.run(document.body).then((results) => {
				for (const violation of results.violations) {
					console.warn(`[axe] ${violation.id}: ${violation.help}`, violation.helpUrl);
				}
			});
		}, 1000);
	});
}

startTransition(() => {
	hydrateRoot(
		document,
		<StrictMode>
			<HydratedRouter />
		</StrictMode>,
	);
});

const posthogToken = import.meta.env.VITE_POSTHOG_PROJECT_TOKEN;
if (posthogToken) {
	posthog.init(posthogToken, {
		api_host: import.meta.env.VITE_POSTHOG_HOST ?? 'https://eu.i.posthog.com',
		autocapture: false,
		capture_pageview: false,
		capture_pageleave: false,
		disable_persistence: true,
		disable_session_recording: true,
		person_profiles: 'never',
		capture_exceptions: true,
		before_send: scrubPostHogErrorEvent,
		loaded: (client) =>
			client.register({
				service: 'tasks',
				environment:
					import.meta.env.VITE_POSTHOG_ENVIRONMENT ?? (import.meta.env.PROD ? 'prod-fe' : 'dev-fe'),
			}),
	});
}
